/**
 * MaterialSearch Component
 *
 * Autocomplete search for inventory materials.
 * Shows available stock and allows selection for material requests.
 *
 * @module pages/technician/materials/MaterialSearch
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Collapse, Form } from 'react-bootstrap';
import { listInventory } from '../../../services/inventoryService';
import { isLowStock } from '../../../utils/calculations';
import PropTypes from 'prop-types';

const STOCK_FILTER_OPTIONS = [
  { value: 'all', label: 'Todo el stock' },
  { value: 'in_stock', label: 'En stock' },
  { value: 'low_stock', label: 'Stock bajo' },
  { value: 'out_of_stock', label: 'Sin stock' },
];

const MaterialSearch = ({
  onSelect,
  selectedMaterial = null,
  disabled = false,
  placeholder = 'Buscar material por nombre o SKU...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [stockFilter, setStockFilter] = useState('all');
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Search for materials with debounce
  const searchMaterials = useCallback(async (term, stockStatus) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const filters = { search: term, pageSize: 15 };
      if (stockStatus === 'low_stock') filters.lowStock = true;

      const { data, error } = await listInventory(filters);
      if (error) {
        console.error('Search error:', error);
        setResults([]);
      } else {
        let filtered = data || [];
        if (stockStatus === 'in_stock') {
          filtered = filtered.filter(item => item.current_stock > 0);
        } else if (stockStatus === 'out_of_stock') {
          filtered = filtered.filter(item => item.current_stock === 0);
        }
        setResults(filtered);
      }
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchMaterials(searchTerm, stockFilter);
      }, 300);
    } else {
      setResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, stockFilter, searchMaterials]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelect = (material) => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setHighlightedIndex(-1);
    onSelect(material);
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getStockClass = (item) => {
    if (item.current_stock === 0) return 'out';
    if (isLowStock(item.current_stock, item.min_stock_level)) return 'low';
    return '';
  };

  // Don't show search if material is already selected
  if (selectedMaterial) {
    return null;
  }

  const activeFilterCount = stockFilter !== 'all' ? 1 : 0;

  return (
    <div className="material-search-container" ref={searchRef}>
      <div className="material-search-bar">
        <i className="bi bi-search material-search-icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="form-control material-search-input"
          placeholder={disabled ? 'Selecciona un proyecto primero...' : placeholder}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowResults(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            if (searchTerm.length >= 2) {
              setShowResults(true);
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-label="Buscar material"
          aria-autocomplete="list"
          aria-controls="material-search-results"
          aria-expanded={showResults}
        />
        {searchTerm && (
          <button
            type="button"
            className="material-search-clear"
            onClick={handleClear}
            aria-label="Limpiar busqueda"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        )}
        <button
          type="button"
          className={`material-search-advanced-toggle ${activeFilterCount > 0 ? 'has-filters' : ''}`}
          onClick={() => setShowAdvanced(!showAdvanced)}
          aria-expanded={showAdvanced}
          aria-label="Busqueda avanzada"
          title="Busqueda avanzada"
        >
          <i className={`bi bi-sliders ${showAdvanced ? 'text-primary' : ''}`}></i>
          {activeFilterCount > 0 && (
            <span className="filter-count-dot">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Advanced Search Panel */}
      <Collapse in={showAdvanced}>
        <div className="material-advanced-filters">
          <div className="advanced-filters-row">
            <Form.Group className="advanced-filter-group">
              <Form.Label className="small text-muted mb-1">Estado de Stock</Form.Label>
              <Form.Select
                size="sm"
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                {STOCK_FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary clear-advanced-btn"
                onClick={() => setStockFilter('all')}
              >
                <i className="bi bi-x-lg me-1"></i>Limpiar
              </button>
            )}
          </div>
        </div>
      </Collapse>

      {showResults && (
        <div
          id="material-search-results"
          className="material-search-results"
          role="listbox"
        >
          {isLoading ? (
            <div className="search-loading">
              <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">Buscando...</span>
              </Spinner>
            </div>
          ) : results.length > 0 ? (
            results.map((item, index) => (
              <div
                key={item.id}
                className={`search-result-item ${
                  highlightedIndex === index ? 'selected' : ''
                }`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={highlightedIndex === index}
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="search-result-image"
                  />
                ) : (
                  <div className="search-result-placeholder">
                    <i className="bi bi-box-seam"></i>
                  </div>
                )}
                <div className="search-result-info">
                  <div className="search-result-name">{item.name}</div>
                  {item.sku && (
                    <div className="search-result-sku">{item.sku}</div>
                  )}
                </div>
                <div className="search-result-stock">
                  <div
                    className={`search-result-stock-value ${getStockClass(
                      item
                    )}`}
                  >
                    {item.current_stock}
                  </div>
                  <div className="search-result-unit">{item.unit}</div>
                </div>
              </div>
            ))
          ) : searchTerm.length >= 2 ? (
            <div className="search-no-results">
              <i className="bi bi-inbox"></i>
              <p>No se encontraron materiales</p>
            </div>
          ) : (
            <div className="search-no-results">
              <i className="bi bi-search"></i>
              <p>Escribe al menos 2 caracteres para buscar</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

MaterialSearch.propTypes = {
  onSelect: PropTypes.func.isRequired,
  selectedMaterial: PropTypes.object,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default MaterialSearch;
